import { useEffect } from 'react';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logoImg from '../logo.png';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1.5} component="section">
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'fontWeightMedium', mt: 4 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body1" color="text.primary">
      {children}
    </Typography>
  );
}

function Subheading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 'fontWeightMedium', mt: 2 }}>
      {children}
    </Typography>
  );
}

function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Box sx={{ borderBottom: (theme) => `solid 1px ${theme.palette.divider}`, py: 2 }}>
        <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="img" src={logoImg} alt="Onesauce" sx={{ width: 28 }} />
            <Typography variant="h6" sx={{ letterSpacing: '-0.5px' }}>
              Onesauce
            </Typography>
          </Box>
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon sx={{ fontSize: '1rem' }} /> Back to home
          </Link>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'fontWeightMedium' }} gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: May 23, 2026
        </Typography>

        <Stack spacing={3}>
          <Body>
            One Sauce, Inc. (“One Sauce,” “we,” “our,” and/or “us”) values the privacy of individuals
            who use our application, website (including www.onesauce.ai), and related services
            (our “Services”). This privacy policy (the “Privacy Policy”) explains how we collect,
            use, and disclose personal information from users of our Services (“you” or “your”).
            By using our Services, you agree to the collection, use, disclosure, and procedures
            described in this Privacy Policy. Beyond the Privacy Policy, your use of our Services
            is also subject to our Terms of Service. If you are a California resident, please also
            review our California Resident Privacy Notice for more information about the types of
            personal information we collect and disclose, as well as how to exercise your rights
            under California law.
          </Body>

          <Section title="Personal Information We Collect">
            <Body>
              We may collect a variety of personal information from or about you or your devices
              from various sources, as described below.
            </Body>

            <Subheading>A. Personal Information You Provide to Us</Subheading>
            <Body>
              <strong>Registration and Profile Information.</strong> When you register to use our
              Services, we ask you for your name, email address, and other firmographic information.
              If you sign up using a third-party account, we will also receive information from
              those third-party accounts.
            </Body>
            <Body>
              <strong>Communications.</strong> If you contact us directly, we may receive personal
              information about you. For example, when you contact us for more information about
              OneSauce, we may receive your name, email address, company name, the contents of a
              message or attachments that you may send to us, and other information you choose to
              provide.
            </Body>
            <Body>
              <strong>Careers.</strong> If you decide that you wish to apply for a job with us, you
              may submit your contact information and your resume online. We will collect the
              information you choose to provide on your resume, such as your education and
              employment experience. You may also apply through LinkedIn. If you do so, we will
              collect the information you make available to us on LinkedIn.
            </Body>

            <Subheading>B. Personal Information We Collect When You Use Our Services</Subheading>
            <Body>
              <strong>Location Information.</strong> When you use our Services, we infer your
              general location information, for example, by using your internet protocol (IP)
              address. With your permission, our mobile app also collects precise location from
              your device's GPS to show nearby venues, surface location-based recommendations, and
              let you check in to accounts you visit. You can revoke location access at any time
              in your device's system settings.
            </Body>
            <Body>
              <strong>Device Information.</strong> We receive information about the device and
              software you use to access our Services, including IP address, web browser type, and
              operating system version.
            </Body>
            <Body>
              <strong>Usage Information.</strong> To help us understand how you use our Services
              and to help us improve it, we automatically receive information about your
              interactions with our Services like pages or other content you view, the searches
              you conduct, and the dates and times of your visits.
            </Body>
            <Body>
              <strong>Your Contacts.</strong> If you permit us to give us access to your contacts
              from an email account associated with your account, we may access and store names
              and contact information from your address book to facilitate interactions that you
              initiate through our Services and for other purposes described in this privacy
              policy or at the time of consent or collection.
            </Body>
            <Body>
              <strong>Call and Video Recording Information.</strong> When you participate in a call
              or video conference with us, we may record the session and retain recordings of the
              session. When we record, you will be notified that the call is being recorded and be
              asked to consent.
            </Body>
            <Body>
              <strong>Information from Cookies and Similar Technologies.</strong> We and our
              third-party partners collect personal information using cookies, pixel tags, or
              similar technologies. Our third-party partners, such as analytics, may use these
              technologies to collect information about your online activities over time and
              across different services. Cookies are small text files containing a string of
              alphanumeric characters. We may use both session cookies and persistent cookies. A
              session cookie disappears after you close your browser. A persistent cookie remains
              after you close your browser and may be used by your browser on subsequent visits to
              our Services.
            </Body>
            <Body>
              Please review your web browser's “Help” file to learn how you may modify your cookie
              settings. Please note that if you delete or choose not to accept cookies from our
              Services, you may not be able to utilize the features of our Services to their
              fullest potential.
            </Body>

            <Subheading>C. Personal Information We Receive from Third Parties</Subheading>
            <Body>
              OneSauce uses data obtained from the Google API in compliance with the Google API
              Services User Data Policy.
            </Body>
          </Section>

          <Section title="How We Disclose the Personal Information We Collect">
            <Body>
              <strong>Partners and Affiliates.</strong> We may disclose any information we receive
              to our partners, corporate affiliates, parents, or subsidiaries for any purpose
              described in this Privacy Policy.
            </Body>
            <Body>
              <strong>Vendors and Service Providers.</strong> We may disclose any information we
              receive to vendors and service providers retained in connection with the provision
              of our Services.
            </Body>
            <Body>
              <strong>Third-Party App Integrations.</strong> If you connect a third-party
              application to our Services, we may disclose information to that third party.
            </Body>
            <Body>
              <strong>Analytics Partners.</strong> We use analytics services such as Google
              Analytics to collect and process certain analytics data. These services may also
              collect information about your use of other websites, apps, and online resources.
              You can learn more about Google's practices by visiting{' '}
              <Link href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener">
                https://www.google.com/policies/privacy/partners/
              </Link>
              . We also use Sentry to receive crash reports and diagnostic information that helps
              us identify and fix software defects.
            </Body>
            <Body>
              <strong>As Required by Law and Similar Disclosures.</strong> We may access, preserve,
              and disclose your personal information if we believe doing so is required or
              appropriate to: (a) comply with law enforcement requests and legal process, such as
              a court order or subpoena; (b) respond to your requests; or (c) protect your, our,
              or others' rights, property, or safety. For the avoidance of doubt, the disclosure
              of your personal information may occur if you post any objectionable content on or
              through our Services.
            </Body>
            <Body>
              <strong>Merger, Sale, or Other Asset Transfers.</strong> We may transfer or disclose
              your personal information to service providers, advisors, potential transactional
              partners, or other third parties in connection with the consideration, negotiation,
              or completion of a corporate transaction in which we are acquired by or merged with
              another company, or we sell, liquidate, or transfer all or a portion of our assets.
            </Body>
            <Body>
              <strong>Consent.</strong> We may also disclose personal information from or about you
              or your devices with your permission.
            </Body>
            <Body>
              <strong>Information Shared with Third Party AI Models.</strong> Data shared with
              third party AI models includes:
            </Body>
            <Box component="ul" sx={{ pl: 4, m: 0 }}>
              <li><Typography variant="body1">Your firmographic data</Typography></li>
              <li><Typography variant="body1">Your tone of voice</Typography></li>
              <li><Typography variant="body1">Products & services you provide</Typography></li>
              <li><Typography variant="body1">Emails</Typography></li>
            </Box>
            <Body>
              This data is shared with third party AI models to write and process emails on your
              behalf.
            </Body>
          </Section>

          <Section title="Your Choices">
            <Body>
              <strong>Marketing Emails.</strong> You can unsubscribe from our promotional emails
              via the link provided in the emails. Even if you opt out of receiving promotional
              messages from us, you will continue to receive administrative messages from us.
            </Body>
            <Body>
              <strong>Do Not Track.</strong> There is no accepted standard on how to respond to Do
              Not Track signals, and we do not respond to such signals.
            </Body>
            <Body>
              If you choose not to provide us with information we request, some features of our
              Services may not work as intended.
            </Body>
          </Section>

          <Section title="Data Retention">
            <Body>
              We take measures to delete your information or keep it in a form that does not
              permit identifying you when this information is no longer necessary for the purposes
              for which we process it, unless we are required by law to keep this information for
              a longer period. When determining the retention period, we take into account various
              criteria, such as the type of products and services requested by or provided to you,
              the nature and length of our relationship with you, the impact on the Services we
              provide to you if we delete some information from or about you, mandatory retention
              periods provided by law, and any relevant statute of limitations.
            </Body>
          </Section>

          <Section title="Account Deletion">
            <Body>
              You may delete your account at any time by emailing{' '}
              <Link href="mailto:privacy@onesauce.ai">privacy@onesauce.ai</Link>. Deletion removes
              your profile, lists, and saved notes within 30 days, except where we are required to
              retain certain records to comply with law, resolve disputes, or enforce our
              agreements.
            </Body>
          </Section>

          <Section title="Your Privacy Rights">
            <Body>
              Subject to applicable law, you may have the right to: (a) access the personal
              information we hold about you; (b) request that we correct inaccurate personal
              information; (c) request that we delete your personal information; (d) receive a
              copy of your personal information in a portable format; and (e) withdraw your
              consent where our processing relies on consent. To exercise any of these rights,
              email us at <Link href="mailto:privacy@onesauce.ai">privacy@onesauce.ai</Link>. We
              will verify your identity before responding and will respond within the time
              required by applicable law. We will not discriminate against you for exercising any
              of these rights.
            </Body>
          </Section>

          <Section title="Third Parties">
            <Body>
              Our Services may contain links to other websites, products, or services that we do
              not own or operate. We are not responsible for the privacy practices of these third
              parties. Please be aware that this Privacy Policy does not apply to your activities
              on these third-party services or any information you disclose to these third
              parties. We encourage you to read their privacy policies before providing any
              information to them.
            </Body>
          </Section>

          <Section title="Security">
            <Body>
              We make reasonable efforts to protect your personal information by using physical
              and electronic safeguards designed to improve the security of the personal
              information we maintain. However, because no electronic transmission or storage of
              information can be entirely secure, we can make no guarantees as to the security or
              privacy of your personal information.
            </Body>
          </Section>

          <Section title="Children's Privacy">
            <Body>
              We do not knowingly collect, maintain, or use personal information from children
              under 13 years of age, and no parts of our Services are directed to children. If you
              learn that a child has provided us with personal information in violation of this
              Privacy Policy, you may alert us at{' '}
              <Link href="mailto:privacy@onesauce.ai">privacy@onesauce.ai</Link>.
            </Body>
          </Section>

          <Section title="Your California Privacy Rights">
            <Body>
              If you are a California resident, the California Consumer Privacy Act, as amended by
              the California Privacy Rights Act (collectively, the “CCPA”), provides you with the
              following rights regarding your personal information:
            </Body>
            <Box component="ul" sx={{ pl: 4, m: 0 }}>
              <li><Typography variant="body1">The right to know the categories and specific pieces of personal information we collect, use, and disclose.</Typography></li>
              <li><Typography variant="body1">The right to request deletion of your personal information.</Typography></li>
              <li><Typography variant="body1">The right to correct inaccurate personal information.</Typography></li>
              <li><Typography variant="body1">The right to opt out of the “sale” or “sharing” of your personal information.</Typography></li>
              <li><Typography variant="body1">The right to limit the use and disclosure of sensitive personal information.</Typography></li>
              <li><Typography variant="body1">The right not to receive discriminatory treatment for exercising these rights.</Typography></li>
            </Box>
            <Body>
              We do not sell your personal information for money. To the extent our use of
              analytics or similar technologies constitutes a “sale” or “sharing” under the CCPA,
              you may opt out by emailing{' '}
              <Link href="mailto:privacy@onesauce.ai">privacy@onesauce.ai</Link> with the subject
              line “Do Not Sell or Share My Personal Information.” You may submit any of the
              requests above to that same address, and you may use an authorized agent to submit a
              request on your behalf. We will verify your request before fulfilling it and will not
              discriminate against you for exercising your rights.
            </Body>
          </Section>

          <Section title="International Visitors">
            <Body>
              Our Services is hosted in the United States (“U.S.”) and intended for visitors
              located within the U.S. If you choose to use our Services from the European Union or
              other regions of the world with laws governing data collection and use that may
              differ from U.S. law, then please note that you are transferring your personal
              information outside of those regions to the U.S. for storage and processing. We may
              also transfer your personal information from the U.S. to other countries or regions
              in connection with storage and processing of data, fulfilling your requests, and
              operating our Services. By providing any information, including personal
              information, on or through our Services, you consent to such transfer, storage, and
              processing.
            </Body>
          </Section>

          <Section title="Changes to This Privacy Policy">
            <Body>
              We will post any adjustments to the Privacy Policy on this page, and the revised
              version will be effective when it is posted. If we materially change the ways in
              which we use or disclose personal information previously collected from you through
              our Services, we will notify you through our Services, by email, or other
              communication.
            </Body>
          </Section>

          <Section title="Contact Information">
            <Body>
              If you have any questions, comments, or concerns about our processing activities,
              please email us at{' '}
              <Link href="mailto:privacy@onesauce.ai">privacy@onesauce.ai</Link> or write to us at:
            </Body>
            <Body>
              One Sauce, Inc.
              <br />
              651 N Broad St, Suite 201
              <br />
              Middletown, DE 19709
              <br />
              United States
            </Body>
          </Section>
        </Stack>
      </Container>
    </Box>
  );
}

export default Privacy;
